import { refreshViaAuth } from '@/lib/auth/oauth';
import { authSession, useAuthStore } from '@/features/auth/stores/auth.store';
import type { AuthTokens } from '@/features/auth/types/auth.types';

/**
 * Single-flight token refresh — directly against the auth server (public client,
 * no secret). Mirrors the mobile app's coalescing: concurrent 401s share one
 * refresh (Passport refresh tokens are single-use). On failure the session is
 * cleared. A fresh EasyAuth token is co-issued for the registry's default v2
 * host (the `azure` scope is preserved); the hosts issued on demand are kept —
 * see `mergeZumoHosts`. Resolves with the grant as the server issued it, while
 * the store holds the merged bundle.
 */
let inFlight: Promise<AuthTokens | null> | null = null;

export function refreshTokens(): Promise<AuthTokens | null> {
  if (inFlight) return inFlight;

  inFlight = (async () => {
    const refreshToken = authSession.getRefreshToken();
    if (!refreshToken) return null;

    try {
      const tokens = await refreshViaAuth(refreshToken);
      if (tokens) {
        const store = useAuthStore.getState();
        store.setTokens(mergeZumoHosts(store.tokens, tokens));
        return tokens;
      }
      useAuthStore.getState().clear();
      return null;
    } catch {
      useAuthStore.getState().clear();
      return null;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

/**
 * The grant carries the EasyAuth token of ONE host; the tokens the session
 * fetched on demand for other hosts (`lib/medis/zumoToken.ts`) are still valid
 * — a refresh never changes the user — so they are kept and only the grant's
 * host is replaced. Login stays wholesale (`setTokens` with the fresh bundle)
 * and logout clears, so a map never outlives its account.
 */
function mergeZumoHosts(previous: AuthTokens | null, fresh: AuthTokens): AuthTokens {
  const hosts = { ...previous?.xZumoAuthHosts, ...fresh.xZumoAuthHosts };
  return { ...fresh, xZumoAuthHosts: Object.keys(hosts).length ? hosts : null };
}
